import { AxiosError } from 'axios';
import { profileAxiosInstance as network } from '../network/axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Endpoints, SiteTypes } from '../network/enums';
import { endpointReplace } from '../network/utils/endpointReplace';
import { useUpdateBroadcastChannel } from '@mvecom/common/hooks/useUpdateBroadcastChannel';
import { Actions } from '../actions/enums';

export const useLogin = (isModalOpen: boolean, onLogin?: () => void) => {
  const [loggedIn, setLoggedIn] = useState<boolean>();
  const [authError, setAuthError] = useState<AxiosError>();
  const [loginError, setLoginError] = useState<AxiosError>();
  const [loginLoading, setLoginLoading] = useState<boolean>(false);

  // listen to iframe post messages
  useEffect(() => {
    const ev = (event: MessageEvent) => {
      const pubsubStart = 'pubsub::';

      if (event.data === pubsubStart + Actions.USER_LOGGED_OUT) {
        setLoggedIn(false);
      }

      // mostly for completion, shouldn't really happen
      if (event.data === pubsubStart + Actions.USER_LOGGED_IN) {
        setLoggedIn(true);
        onLogin?.();
      }
    };

    window.addEventListener('message', ev);
    return () => window.removeEventListener('message', ev);
  }, [onLogin]);

  // listen to other tabs updating
  const broadcast = useUpdateBroadcastChannel('profile_pages_update', (message) => {
    if (message.type === 'profile') {
      if (message.action === 'signed-in') {
        setLoggedIn(true);
        onLogin?.();
      } else if (message.action === 'signed-out') {
        setLoggedIn(false);
      }
    }
  });

  // check login on start up
  useEffect(() => {
    if (!isModalOpen) return;

    let aborted = false;

    network
      .get(endpointReplace({ ':siteType': SiteTypes.PROFILE }, Endpoints.Auth))
      .then((isLoggedIn) => {
        if (!aborted) {
          setLoggedIn(!!isLoggedIn.data);

          if (isLoggedIn) {
            window?.ikea?.pubsub?.publish?.(Actions.USER_INFO_AVAILABLE);
          }
        }
      })
      .catch((e) => {
        setAuthError(e);
      });

    return () => {
      aborted = true;
    };
  }, [isModalOpen]);

  // login using email and password, send all the events
  const login = useCallback(
    (email: string, password: string) => {
      setLoginLoading(true);
      network
        .post(endpointReplace({ ':siteType': SiteTypes.PROFILE }, Endpoints.Auth), {
          password,
          userName: email,
        })
        .then(({ status, data }) => {
          const loggedIn = status >= 200 && status <= 202;
          setLoggedIn(loggedIn);

          if (loggedIn) {
            broadcast({
              type: 'profile',
              action: 'signed-in',
            });
            window?.ikea?.pubsub?.publish?.(Actions.USER_LOGGED_IN);
            onLogin?.();
          }

          window?.sendEvent?.({
            custom: {
              user_id: data.userId,
            },
            event_action: 'logins_regular_profile',
            event_category: 'user_actions',
            event_label: loggedIn ? 'success' : 'fail',
            non_interaction: false,
          });
        })
        .catch(setLoginError)
        .finally(() => setLoginLoading(false));
    },
    [broadcast, onLogin],
  );

  return useMemo(
    () => ({
      login,
      loggedIn: !!loggedIn,
      loadingLoggedIn: loggedIn === undefined,
      loginLoading,
      loginError,
      authError,
    }),
    [authError, loggedIn, login, loginError, loginLoading],
  );
};
